import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Download, Upload, Pencil } from "lucide-react";
import { useSupplierEvaluation, GradeBadge, SupplierEvaluationDialog } from "../components/SupplierEvaluation";

const EMPTY = { type: "supplier", name: "", tax_id: "", contact: "", email: "", phone: "", address: "", default_currency: "MXN", tax_rate: 16, payment_terms: "", active: true };

export default function AddressBook() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const evaluations = useSupplierEvaluation(true);
  const [evalOpen, setEvalOpen] = useState(null);
  const [currencies, setCurrencies] = useState([{ code: "MXN" }]);

  const load = useCallback(() => {
    const params = {};
    if (filter !== "all") params.type = filter;
    if (q) params.q = q;
    api.get("/address-book", { params }).then((r) => setRows(r.data));
  }, [filter, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/currencies").then((r) => setCurrencies(r.data)).catch(() => {}); }, []);

  const save = async () => {
    try {
      if (editId) await api.put(`/address-book/${editId}`, form);
      else await api.post("/address-book", form);
      toast.success(t("common.saved"));
      setOpen(false); setForm(EMPTY); setEditId(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const downloadTemplate = async () => {
    const { data } = await api.get("/address-book-template");
    const blob = new Blob([data], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "address_book_template.csv";
    a.click();
  };

  const importCsv = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/address-book-import", fd);
      toast.success(`${t("common.created")}: ${data.created}`);
      if (data.errors.length) toast.warning(data.errors.slice(0, 3).join("; "));
      load();
    } catch (err) { toast.error(errMsg(err)); }
    e.target.value = "";
  };

  const editable = can("admin", "buyer", "warehouse");
  const field = (key, label, type = "text") => (
    <div className="space-y-1">
      <Label className="text-[10px] font-bold uppercase tracking-widest">{label}</Label>
      <Input type={type} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        data-testid={`ab-form-${key}`} className="rounded-none h-8 text-xs" />
    </div>
  );

  return (
    <div data-testid="address-book-page">
      <PageHeader title={t("ab.title")} subtitle={t("ab.subtitle")}>
        <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={downloadTemplate} data-testid="ab-template-button">
          <Download size={13} className="mr-1" /> {t("common.template")}
        </Button>
        {editable && (
          <label className="cursor-pointer">
            <input type="file" accept=".csv" className="hidden" onChange={importCsv} data-testid="ab-import-input" />
            <span className="inline-flex items-center h-8 border px-3 text-xs font-medium hover:bg-muted transition-colors">
              <Upload size={13} className="mr-1" /> {t("common.importCsv")}
            </span>
          </label>
        )}
        {editable && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button size="sm" className="rounded-none text-xs font-bold" data-testid="ab-new-button">
                <Plus size={13} className="mr-1" /> {t("ab.new")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="uppercase font-black">{editId ? t("common.edit") : t("ab.new")}</DialogTitle>
                <DialogDescription className="text-xs">{t("ab.subtitle")}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("ab.type")}</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid="ab-form-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="supplier">{t("ab.supplier")}</SelectItem>
                      <SelectItem value="employee">{t("ab.employee")}</SelectItem>
                      <SelectItem value="customer">{t("ab.customer")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {field("name", t("ab.name"))}
                {field("tax_id", t("ab.taxId"))}
                {field("contact", t("ab.contact"))}
                {field("email", "Email", "email")}
                {field("phone", t("ab.phone"))}
                <div className="col-span-2">{field("address", t("ab.address"))}</div>
                {form.type === "supplier" && (
                  <>
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">{t("ab.currency")}</Label>
                      <Select value={form.default_currency || "MXN"} onValueChange={(v) => setForm({ ...form, default_currency: v })}>
                        <SelectTrigger className="rounded-none h-8 text-xs" data-testid="ab-form-currency"><SelectValue /></SelectTrigger>
                        <SelectContent>{currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">{t("ab.taxRate")}</Label>
                      <Select value={String(form.tax_rate ?? 16)} onValueChange={(v) => setForm({ ...form, tax_rate: Number(v) })}>
                        <SelectTrigger className="rounded-none h-8 text-xs" data-testid="ab-form-tax-rate"><SelectValue /></SelectTrigger>
                        <SelectContent>{[16, 8, 0].map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {field("payment_terms", t("ab.paymentTerms"))}
                  </>
                )}
              </div>
              <Button onClick={save} disabled={!form.name} className="rounded-none font-bold" data-testid="ab-save-button">{t("common.save")}</Button>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <div className="flex gap-2 mb-4 flex-wrap">
        {["all", "supplier", "employee", "customer"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`ab-filter-${f}`}
            className={`border px-3 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors ${filter === f ? "bg-foreground text-background" : "hover:bg-muted"}`}>
            {f === "all" ? t("common.all") : t(`ab.${f}`)}
          </button>
        ))}
        <Input placeholder={t("common.search")} value={q} onChange={(e) => setQ(e.target.value)}
          className="rounded-none h-7 text-xs max-w-52 ml-auto" data-testid="ab-search-input" />
      </div>

      {rows.length === 0 ? <Empty text={t("common.noData")} /> : (
        <div className="tbl-wrap" data-testid="ab-table">
          <table className="tbl">
            <thead><tr>
              <th>#</th><th>{t("ab.type")}</th><th>{t("ab.name")}</th><th>{t("ab.taxId")}</th>
              <th>{t("ab.contact")}</th><th>Email</th><th>{t("ab.phone")}</th><th>{t("ab.evaluation")}</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="font-bold">{r.ab_number}</td>
                  <td><span className="uppercase text-[10px] font-bold text-muted-foreground">{t(`ab.${r.type}`)}</span></td>
                  <td className="font-medium">{r.name}</td>
                  <td>{r.tax_id}</td><td>{r.contact}</td><td>{r.email}</td><td>{r.phone}</td>
                  <td>{r.type === "supplier" && <GradeBadge ev={evaluations.find((e) => e.supplier_id === r.id)} onClick={() => setEvalOpen(evaluations.find((e) => e.supplier_id === r.id))} />}</td>
                  <td>
                    {editable && (
                      <button data-testid={`ab-edit-${r.ab_number}`} className="text-muted-foreground hover:text-primary"
                        onClick={() => { setForm({ type: r.type, name: r.name, tax_id: r.tax_id, contact: r.contact, email: r.email, phone: r.phone, address: r.address, default_currency: r.default_currency || "MXN", tax_rate: r.tax_rate ?? 16, payment_terms: r.payment_terms || "", active: r.active }); setEditId(r.id); setOpen(true); }}>
                        <Pencil size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <SupplierEvaluationDialog ev={evalOpen} onClose={() => setEvalOpen(null)} />
    </div>
  );
}
